import { KeyFeature } from '~/models/classified/types'
import { CategoryId } from '~/models/category/types'
import {
  INSTALLMENTS_DOWNPAYMENT_PARAM_NAME,
  INSTALLMENTS_NUM_OF_INSTALLMENTS_PARAM_NAME
} from '~/constants/finance/installments'
import {
  LEASING_DOWNPAYMENT_PARAM_NAME,
  LEASING_DURATION_PARAM_NAME,
  LEASING_LEASE_PARAM_NAME,
  LEASING_MILEAGE_PARAM_NAME,
  LEASING_WITH_VAT_PARAM_NAME
} from '~/constants/finance/leasing'
import qs from 'qs'
import { computed, onBeforeUnmount, ref } from '~/utils/nuxt3-migration'
import { useNamespacedStore } from '~/compositions/store'
import {
  CLASSIFIED_SEARCH_NS,
  SearchState
} from '~/store/modules/shared/classifieds/search/state'
import { UserType } from '~/models/user/types'
import { SubscriptionTier } from '~/models/search/subscriptions/types'
import { USER_NS } from '~/store/modules/shared/user/state'
import { CLASSIFIED_VIEW_NS } from '~/store/modules/shared/classifieds/view/state'
import { toCamelCase } from '~/utils/object'
import { MODIFY_ROW_PROMOTION_ACTIVE } from '~/store/modules/shared/classifieds/search/mutation-types'
import { ActivePromotion } from '~/models/classified/promoted'
import { ciCalendar } from '~/icons/source/light/calendar'
import { ciMileage } from '~/icons/source/light/mileage'
import { ciEngine } from '~/icons/source/light/engine'
import { ciDashboard } from '~/icons/source/light/dashboard'
import { ciDoors } from '~/icons/source/light/doors'
import { ciTransmission } from '~/icons/source/light/transmission'
import { ciFuel } from '~/icons/source/light/fuel'
import { ciAutonomy } from '~/icons/source/light/autonomy'
import { ciChargingTime } from '~/icons/source/light/charging-time'
import { ciSeats } from '~/icons/source/light/seats'
import { ciCheckCircle } from '~/icons/source/light/check-circle'
import { CIcon } from '~/icons/types'
import { Timeout } from '~/models/timing/timeout'
import { PAGE_NS } from '~/store/modules/shared/page/state'
import { useRouter } from '@nuxtjs/composition-api'
import { DEALER_SITE_NS } from '~/store/modules/shared/dealers/site/state'
import { ciAxles } from '~/icons/source/light/axles'
import { ciWeight } from '~/icons/source/light/weight'
import { ciMotoEngine } from '~/icons/source/light/moto-engine'
import { ciClock } from '~/icons/source/light/clock'
import { ciAbs } from '~/icons/source/light/abs'
import { ciBarcode } from '~/icons/source/light/barcode'
import { ciPayload } from '~/icons/source/light/payload'
import { ciLength } from '~/icons/source/light/length'
import { ciHeight } from '~/icons/source/light/height'
import {
  adminTimeDistanceFromNow,
  cappedTimeDistanceFromNow
} from '~/utils/date'
import { LocaleCode } from '~/models/locale/types'
import { useI18n } from '~/compositions/i18n'
import { ClassifiedSearchResultRow } from '~/models/search/types'
import { ciBathroom } from '~/icons/source/light/bathroom'
import { ciBedroom } from '~/icons/source/light/bedroom'
import { ciConstructionYear } from '~/icons/source/light/construction-year'
import { ciSquareMeters } from '~/icons/source/light/square-meters'
import { ciStairs } from '~/icons/source/light/stairs'
import { ciPropeller } from '~/icons/source/light/propeller'

const keyFeaturesIcons: Record<KeyFeature['key'], CIcon> = {
  age: ciCalendar,
  registration: ciCalendar,
  mileage: ciMileage,
  max_mileage: ciMileage,
  engine_size: ciEngine,
  engine_power: ciDashboard,
  doors: ciDoors,
  gearbox_type: ciTransmission,
  gears: ciTransmission,
  fuel_type: ciFuel,
  battery_range: ciAutonomy,
  battery_charge_time: ciChargingTime,
  passengers: ciSeats,
  axles: ciAxles,
  weight: ciWeight,
  payload: ciPayload,
  abs: ciAbs,
  makemodel: ciBarcode,
  length: ciLength,
  height: ciHeight,
  bathrooms: ciBathroom,
  bedrooms: ciBedroom,
  year_of_construction: ciConstructionYear,
  area: ciSquareMeters,
  floor: ciStairs,
  engine_type: ciPropeller
}

export function useClassifiedSearchResultRow(
  classified: ClassifiedSearchResultRow
) {
  const {
    state: classifiedSearchState,
    getters: classifiedSearchGetters,
    commit: classifiedSearchCommit
  } = useNamespacedStore<SearchState>(CLASSIFIED_SEARCH_NS)
  const { dispatch: classifiedViewDispatch } = useNamespacedStore(
    CLASSIFIED_VIEW_NS
  )
  const { getters: userGetters } = useNamespacedStore(USER_NS)
  const { getters: pageGetters } = useNamespacedStore(PAGE_NS)
  const { getters: dealerSiteGetters } = useNamespacedStore(DEALER_SITE_NS)
  const router = useRouter()
  const { locale } = useI18n()

  const currentImageIndex = ref(0)
  const imageLoading = ref(false)
  const touchStartX = ref<number | null>(null)
  const isTouchingImage = ref(false)
  const isVehicleClassified = classified.categoryIds.includes(
    CategoryId.VEHICLES
  )
  const isPartClassified = classified.categoryIds.includes(CategoryId.PARTS)
  const isXymaClassified = classified.categoryIds.includes(CategoryId.XYMA)
  const isBikeClassified = classified.categoryIds.includes(CategoryId.BIKES)
  const isTractorClassified = classified.categoryIds.includes(
    CategoryId.TRACTORS
  )
  const isBoatClassified = classified.categoryIds.includes(CategoryId.BOATS)
  const isRentalClassified = classified.categoryIds.includes(CategoryId.RENTALS)
  let hideImageIndicatorTimeout: Timeout | null = null

  // Clean up the timeout when component is unmounted
  onBeforeUnmount(() => {
    if (hideImageIndicatorTimeout) {
      clearTimeout(hideImageIndicatorTimeout)
    }
  })

  const userIsAdmin = computed(() => userGetters('isAdmin'))

  const userIsManager = computed(() => userGetters('isManager'))

  const currentRoute = computed(() => classifiedSearchState.currentRoute)

  const isEshopOnboardingSearch = computed(
    () => currentRoute.value.query.eshop_onboarding
  )

  const isDealsSearch = computed(
    () => classifiedSearchState.config.settings.isDealsSearch
  )

  const isDeletedSearch = computed(() =>
    classifiedSearchGetters('isDeletedSearch')
  )

  const isAdminSearch = computed(
    () => classifiedSearchState.config.settings.isAdminSearch
  )

  const isUserProfileSearch = computed(() => classifiedSearchState.userProfile)

  const inDealerSiteSearch = computed(() =>
    classifiedSearchGetters('inDealerSiteSearch')
  )

  const rootCategoryId = computed(() =>
    classifiedSearchGetters('getCurrentCategoryId')
  )

  const showActions = computed(() => {
    if (isEshopOnboardingSearch.value) {
      return false
    }

    return classified.own || userIsAdmin.value
  })

  const isPromoted = computed(
    () =>
      classified.fromPromotion ||
      classified.promotion?.activePromotion?.isPromoted
  )

  const classifiedRequiresPayment = computed(() => {
    if (!classified.paidClassified) {
      return false
    }

    return (
      classified.paidClassified.requiresPayment ||
      classified.paidClassified.hasPaymentFailure
    )
  })

  const classifiedBackgroundJobs = computed(() => {
    if (!classified.jobs) {
      return null
    }

    return classified.jobs.succeed.length || classified.jobs.failed.length
      ? toCamelCase(classified.jobs)
      : null
  })

  const showSellerInfo = computed(() => {
    const { seller } = classified
    const sellerIsDealer =
      seller.type === UserType.MANAGER || seller.type === UserType.DEALER

    if (!sellerIsDealer) {
      return false
    }

    const isSellerInGoldSubscriptionTier =
      seller.subscriptionTier === SubscriptionTier.GOLD

    return isPromoted.value || isSellerInGoldSubscriptionTier
  })

  const href = computed(() => {
    if (isEshopOnboardingSearch.value) {
      return `/classifieds/${classified.id}/edit/eshop/`
    }

    if (inDealerSiteSearch.value) {
      const activeDealerSitePage = dealerSiteGetters('activePage')
      const resolvedRoute = router.resolve({
        name: '__dealers_site_classified',
        params: {
          slugOrId: classified.id.toString(),
          searchPath: activeDealerSitePage?.pagePath
        }
      })

      return resolvedRoute.href
    }

    const classifiedViewHref = classified.seoUrl || `/${classified.id}/`
    let financeQueryParams = {}
    let leasingQueryParams = {}
    let rentalQueryParams = {}

    const { installment, leasing } = classified

    if (installment) {
      const { installments, downpayment } = currentRoute.value.query
      financeQueryParams = {
        [INSTALLMENTS_NUM_OF_INSTALLMENTS_PARAM_NAME]: installments,
        [INSTALLMENTS_DOWNPAYMENT_PARAM_NAME]: downpayment
      }
    }

    if (leasing && leasing.option) {
      const {
        withVat,
        duration: { value: durationValue },
        mileage: { value: mileageValue },
        downpayment: { value: downpaymentValue },
        lease: { value: leaseValue }
      } = leasing.option

      leasingQueryParams = {
        [LEASING_WITH_VAT_PARAM_NAME]: withVat,
        [LEASING_DURATION_PARAM_NAME]: durationValue,
        [LEASING_MILEAGE_PARAM_NAME]: mileageValue,
        [LEASING_DOWNPAYMENT_PARAM_NAME]: downpaymentValue,
        [LEASING_LEASE_PARAM_NAME]: leaseValue
      }
    }

    if (classified.rental) {
      rentalQueryParams = {
        'rental_period-from': currentRoute.value.query['rental_period-from'],
        'rental_period-to': currentRoute.value.query['rental_period-to']
      }
    }

    const queryParams: Record<string, any> = {
      ...financeQueryParams,
      ...leasingQueryParams,
      ...rentalQueryParams
    }

    if (isDealsSearch.value) {
      queryParams['from-deals'] = true
    }

    const queryParamsStr = qs.stringify(queryParams, {
      arrayFormat: 'repeat',
      encode: false,
      skipNulls: true
    })

    return queryParamsStr
      ? `${classifiedViewHref}?${queryParamsStr}`
      : classifiedViewHref
  })

  const rowBgClass = computed(() => {
    if (isDeletedSearch.value) {
      return isVehicleClassified ? 'tw-bg-white' : 'tw-bg-[#ffd1d9]'
    }

    const { states } = classified

    if (!states) {
      return 'tw-bg-white'
    }

    if (states.isRemoved) {
      return 'tw-bg-red-200'
    } else if (states.isUnlisted) {
      return 'tw-bg-yellow-50'
    } else if (states.isDeleted) {
      return 'tw-bg-red-100'
    } else if (states.isNonPublic) {
      return 'tw-bg-yellow-200'
    } else if (states.isHidden) {
      return 'tw-bg-yellow-100'
    } else if (
      (isAdminSearch.value ||
        (isUserProfileSearch.value && userIsAdmin.value)) &&
      !isVehicleClassified
    ) {
      return 'tw-bg-[#c1f0f5]'
    }

    return 'tw-bg-white'
  })

  const classifiedImg = computed(() => {
    const thumbsPatternsUrls = classified.thumbsPatterns?.urls

    if (!thumbsPatternsUrls?.length) {
      return classified.thumb
    }

    return thumbsPatternsUrls[currentImageIndex.value].replace('{size}', 'n')
  })

  const classifiedImgContainerClass = computed(() => {
    if (userIsManager.value && classified.dealerCls) {
      return 'tw-border-2 tw-border-red-900'
    }

    if (userIsAdmin.value) {
      if (!classified.user || !classified.seller) {
        return ''
      }

      const isPublicDealer = Boolean(classified.user.publicDealer)
      const userIsManagerOrDealer =
        classified.user.type === UserType.MANAGER ||
        classified.user.type === UserType.DEALER

      if (!userIsManagerOrDealer) {
        return ''
      }

      return isPublicDealer
        ? 'tw-border-2 tw-border-red-900'
        : 'tw-border-2 tw-border-green-700'
    }

    return ''
  })

  const isCensored = computed(
    () =>
      classified.consent &&
      !pageGetters('consentIsConfirmed')(classified.consent.name)
  )

  const lastModifiedDate = computed(() =>
    userIsAdmin.value
      ? adminTimeDistanceFromNow(classified.modified)
      : cappedTimeDistanceFromNow(classified.modified, 60, {
          locale: locale as LocaleCode,
          addPrefix: false
        })
  )

  const classifiedViewRefs = computed(() => ({
    categoryIds: classified.categoryIds,
    id: classified.id,
    isParked: classified.isParked,
    thumb: classified.thumb,
    title: classified.title,
    price: classified.price,
    salary: classified.salary,
    payment: classified.payment,
    thumbIndex: 0,
    offertype: classified.offertype,
    hasPhotos: classified.hasPhotos,
    thumbsLength: classified.thumbs ? classified.thumbs.count : 0,
    externalLink: classified.externalLink,
    discount: classified.discount
  }))

  const images = computed(
    () =>
      classified.thumbsPatterns?.urls.map((u: string) =>
        u.replace('{size}', 'n')
      ) || []
  )

  const showCompareButton = computed(() => {
    if (
      !classified.states ||
      !classifiedSearchState.config.settings.isCompareButtonVisible
    ) {
      return false
    }

    return (
      !classified.states.isHidden &&
      !classified.states.isNonPublic &&
      !classified.states.isDeleted &&
      !classified.states.isRemoved
    )
  })

  function onRowClick(evt: Event) {
    if (isEshopOnboardingSearch.value) {
      return
    }

    evt.preventDefault()
    evt.stopPropagation()

    classifiedViewDispatch('navigateToClassifiedView', {
      evt,
      url: href.value,
      refs: classifiedViewRefs.value
    })
  }

  function onSetActivePromotion(promoForClassified: ActivePromotion | null) {
    classifiedSearchCommit(MODIFY_ROW_PROMOTION_ACTIVE, {
      classifiedId: classified.id,
      promo: promoForClassified
    })
  }

  function onMessageClick(evt: Event) {
    classifiedViewDispatch('navigateToClassifiedView', {
      evt,
      url: href.value,
      refs: {
        ...classifiedViewRefs.value,
        goToMessagesOnLoad: true
      }
    })
  }

  function getKeyFeatureIcon(keyFeature: KeyFeature) {
    if (keyFeature.key === 'engine_size' && isBikeClassified) {
      return ciMotoEngine
    }

    if (
      keyFeature.key === 'mileage' &&
      (isBoatClassified || isTractorClassified)
    ) {
      return ciClock
    }

    return keyFeaturesIcons[keyFeature.key] || ciCheckCircle
  }

  function loadPreviousImage() {
    const thumbsPatternsUrls = classified.thumbsPatterns.urls

    const index =
      currentImageIndex.value - 1 < 0
        ? thumbsPatternsUrls.length - 1
        : currentImageIndex.value - 1

    loadImage(index)
  }

  function loadNextImage() {
    const thumbsPatternsUrls = classified.thumbsPatterns.urls

    const index =
      currentImageIndex.value + 1 > thumbsPatternsUrls.length - 1
        ? 0
        : currentImageIndex.value + 1

    loadImage(index)
  }

  function loadImage(index: number) {
    const thumbsPatternsUrls = classified.thumbsPatterns.urls
    imageLoading.value = true

    const loadingIndex = index // Capture the target index

    const img = new Image()
    img.src = thumbsPatternsUrls[index].replace('{size}', 'n')
    img.onload = () => {
      // Only update if this is still the index we want to show
      if (loadingIndex === index) {
        currentImageIndex.value = index
        imageLoading.value = false
      }
    }
    img.onerror = () => {
      if (loadingIndex === index) {
        imageLoading.value = false
      }
    }
  }

  function onImageTouchStart(event: TouchEvent) {
    if (!images.value.length || images.value.length <= 1) return
    touchStartX.value = event.touches[0].clientX
    isTouchingImage.value = true

    // Clear any existing timeout
    if (hideImageIndicatorTimeout) {
      clearTimeout(hideImageIndicatorTimeout)
      hideImageIndicatorTimeout = null
    }
  }

  function onImageTouchEnd(event: TouchEvent) {
    if (
      touchStartX.value === null ||
      !images.value.length ||
      images.value.length <= 1
    ) {
      return
    }

    const touchEndX = event.changedTouches[0].clientX
    const diffX = touchEndX - touchStartX.value

    if (Math.abs(diffX) >= 50) {
      if (diffX > 0) {
        loadPreviousImage()
      } else {
        loadNextImage()
      }
    }

    touchStartX.value = null

    // Set timeout to hide the counter after the user has stopped interacting for some time
    if (hideImageIndicatorTimeout) {
      clearTimeout(hideImageIndicatorTimeout)
    }

    hideImageIndicatorTimeout = setTimeout(() => {
      isTouchingImage.value = false
      hideImageIndicatorTimeout = null
    }, 1500)
  }

  return {
    isVehicleClassified,
    isPartClassified,
    isXymaClassified,
    isRentalClassified,
    isCensored,
    isPromoted,
    showSellerInfo,
    rowBgClass,
    rootCategoryId,
    href,
    showActions,
    classifiedRequiresPayment,
    classifiedBackgroundJobs,
    classifiedImgContainerClass,
    isTouchingImage,
    images,
    imageLoading,
    classifiedImg,
    currentImageIndex,
    showCompareButton,
    lastModifiedDate,
    classifiedViewRefs,
    onRowClick,
    onMessageClick,
    onSetActivePromotion,
    loadNextImage,
    loadPreviousImage,
    onImageTouchStart,
    onImageTouchEnd,
    getKeyFeatureIcon
  }
}
